import { useCallback, useEffect, useState } from 'react'
import { useViewport } from '@lightspeed/design-system-react'
import { usePosStateManager } from '../../../ng-compat/hooks'
import { useAppSelector } from '../../../state/hooks'
import { selectSsboTourActive } from '../../../state/slices/posState/posStateSlice'
import {
  SsboTourStepId,
  SSBO_TOUR_TOTAL_STEPS,
  getSsboTourStep,
  getSsboTourStepNumber,
} from './ssbo-tour-steps'

export const useIsNarrowPayViewport = () => {
  const { ExtraSmall } = useViewport()
  return Boolean(ExtraSmall)
}

export const useIsNarrowLspayTerminalViewport = () => {
  const { ExtraSmall, Small, Medium, Large } = useViewport()
  return Boolean(ExtraSmall || Small || Medium || Large)
}

// Measured cutoff for the medium modal + popover to fit side by side
const HAS_SIDE_PLACEMENT_ROOM_QUERY = '(min-width: 1311px)'

/** Requires an ancestor <ViewportProvider>. */
export const useIsNarrowLspayCnpViewport = () => {
  const { ExtraSmall, Small, Medium, Large, ExtraLarge } = useViewport()
  const hasMatchMedia = typeof window.matchMedia === 'function'
  const [hasSidePlacementRoom, setHasSidePlacementRoom] = useState(() =>
    hasMatchMedia
      ? window.matchMedia(HAS_SIDE_PLACEMENT_ROOM_QUERY).matches
      : true
  )

  useEffect(() => {
    if (!hasMatchMedia) return

    const mediaQuery = window.matchMedia(HAS_SIDE_PLACEMENT_ROOM_QUERY)
    const onChange = (event: MediaQueryListEvent) =>
      setHasSidePlacementRoom(event.matches)

    // addEventListener isn't supported until Safari 14, which is newer than
    // browserslist-config-xseries' Safari >= 10.1 baseline - addListener
    // works across our whole supported range, so use it unconditionally.
    mediaQuery.addListener(onChange)
    return () => mediaQuery.removeListener(onChange)
  }, [hasMatchMedia])

  if (ExtraSmall || Small || Medium || Large) return true
  if (ExtraLarge) return !hasSidePlacementRoom
  return false
}

/** Per-step tour state. No stored step number - the mounted host decides the visible step. */
export const useSsboTour = (stepId: SsboTourStepId) => {
  const posStateManager = usePosStateManager()
  const isActive = useAppSelector(selectSsboTourActive)

  const isNarrowPayViewport = useIsNarrowPayViewport()
  const isNarrowLspayTerminalViewport = useIsNarrowLspayTerminalViewport()
  const isNarrowLspayCnpViewport = useIsNarrowLspayCnpViewport()

  let isNarrowViewport = false
  switch (stepId) {
    case 'pay':
      isNarrowViewport = isNarrowPayViewport
      break
    case 'lspayTerminal':
      isNarrowViewport = isNarrowLspayTerminalViewport
      break
    case 'lspayCnp':
      isNarrowViewport = isNarrowLspayCnpViewport
      break
  }

  const dismiss = useCallback(() => {
    posStateManager.setSsboTourActive(false)
  }, [posStateManager])

  const step = getSsboTourStep(stepId)

  return {
    isActive,
    step:
      step && isNarrowViewport && step.narrowPlacement
        ? { ...step, placement: step.narrowPlacement }
        : step,
    currentStep: getSsboTourStepNumber(stepId),
    totalSteps: SSBO_TOUR_TOTAL_STEPS,
    dismiss,
  }
}
